import React, { useState, useEffect } from "react";
import "../styles/hero.css";
import logo from "../assets/logo.svg"; // import the logo image
import { heroSlidesApi, getImageUrl } from '../api/phpApi';

export default function HeroSection() {
  const fullText = "Nous connectons les idées. Nous créons les événements.";
  const [text, setText] = useState("");
  const [index, setIndex] = useState(0);

  // Slider state and slides list
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false); // <-- new: pause on hover
  const autoplayDelay = 5000; // ms

  // Fetch slides from API & localStorage
  const loadSlides = async () => {
    try {
      const data = await heroSlidesApi.getAll();
      if (Array.isArray(data) && data.length > 0) {
        setSlides(data);
      }
    } catch (error) {
      console.error('Failed to load hero slides:', error);
    }
  };

  useEffect(() => {
    loadSlides();

    // Listen for storage changes from admin panel
    const handleStorageChange = (e) => {
      if (e.key === 'iamar_hero_slides') {
        loadSlides();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Typing effect (unchanged behavior)
  useEffect(() => {
    if (index <= fullText.length) {
      const timeout = setTimeout(() => {
        setText(fullText.slice(0, index));
        setIndex((i) => i + 1);
      }, 40);
      return () => clearTimeout(timeout);
    }
  }, [index]);

  // Autoplay slider — respect isPaused
  useEffect(() => {
    if (isPaused || slides.length === 0) return; // do not start interval when paused or no slides
    const id = setInterval(() => {
      setCurrent((c) => (c + 1) % slides.length);
    }, autoplayDelay);
    return () => clearInterval(id);
  }, [slides.length, isPaused]);

  // Keyboard navigation (left/right)
  useEffect(() => {
    const onKey = (e) => {
      if (slides.length === 0) return;
      if (e.key === "ArrowLeft") {
        setCurrent((c) => (c - 1 + slides.length) % slides.length);
      } else if (e.key === "ArrowRight") {
        setCurrent((c) => (c + 1) % slides.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  const goToSlide = (i) => setCurrent(i);

  const handleScrollToServices = (e) => {
    // Navigate to about page with presentation section
    window.location.href = '/about#presentation';
  };

  if (slides.length === 0) {
    return <div className="hero-loading">Chargement...</div>;
  }

  return (
    <section
      className="hero-section"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Slides */}
      <div className="hero-slides">
        {slides.map((slide, index) => {
          const bgUrl = getImageUrl(slide.image_url || slide.src);
          return (
            <div
              key={slide.id || index}
              className={`hero-slide ${index === current ? 'active' : ''}`}
              style={{
                backgroundImage: bgUrl ? `url("${bgUrl}")` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
          );
        })}
      </div>

      {/* Overlay */}
      <div className="hero-overlay" />

      {/* Content */}
      <div className="hero-content">
        {/* Logo */}
        <div className="hero-logo-container">
          <img src={logo} alt="IAMAR" className="hero-logo" />
        </div>

        {/* Main Content */}
        <div className="hero-main-content">
          <h1 className="hero-title">
            <span className="hero-title-text">{text}</span>
            <span className="hero-cursor">|</span>
          </h1>

          <p className="hero-subtitle">
            {slides[current]?.caption || 'Innovation • Excellence • Impact'}
          </p>

          <div className="hero-actions">
            <button
              className="hero-cta-button"
              onClick={handleScrollToServices}
              aria-label="Découvrir nos services"
            >
              <span>QUI SOMME NOUS</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7"></path>
                <path d="M7 7h10v10"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="hero-navigation">
          <div className="hero-dots">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`hero-dot ${index === current ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>

          <div className="hero-arrows">
            <button
              className="hero-arrow hero-arrow-prev"
              onClick={() => setCurrent((prev) => (prev - 1 + slides.length) % slides.length)}
              aria-label="Slide précédente"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"></path>
              </svg>
            </button>

            <button
              className="hero-arrow hero-arrow-next"
              onClick={() => setCurrent((prev) => (prev + 1) % slides.length)}
              aria-label="Slide suivante"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}